import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Button, Collapse, Grid } from "@mui/material";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "../../../../app/hooks";
import FormSection from "../../../../common/components/functional/formSection/FormSection";
import isEmpty from "../../../../common/helpers/isEmpty";
import { isExpertCustomerServiceOrAdmin } from "../../../user/accessRightChecks";
import OrderShortTesPricingOverride from "../../additionalInfo/OrderShortTesPricingOverride";
import OrderSupplements from "../../additionalInfo/OrderSupplements";
import { AdditionalInfoFormContainer } from "../../additionalInfo/styles";
import TeamNotificationSettings from "../../additionalInfo/TeamNotificationSettings";
import { ShiftOrderContext } from "../../newOrder/short/OrderShortDataProvider";
import { OffersStatusBlock } from "../../offersStatus/OffersStatusBlock";
import { OffersStatus, OrderEdit, ShiftDetails2 } from "../../orderAPI";
import {
  AutocompleteOptions,
  AutomatchingStatus,
  ValidationError,
} from "../../orderTypes";
import {
  CancelAllAssignmentsCallback,
  CancelAssignmentCallback,
  HandleAdditionalInfoChangeFunction,
  HandleAmountChangeFunction,
  HandleAutomatchingStatusChangeFunction,
  HandleAutomatchingDelayChangeFunction,
  HandleCustomerCCChangeFunction,
  HandleDescriptionsChangeFunction,
  HandleFillShiftChangeFunction,
  HandleFlexibleStartTimeChangeFunction,
  HandlePeriodStartDateChangeFunction,
  HandleRequirementsChangeFunction,
  HandleSalaryLimitChangeFunction,
  HandleSkillsChangeFunction,
  HandleSupplementPriceOverrideChangeFunction,
  HandleSupplementsChangeFunction,
  HandleTeamNotificationSettingsChangeFunction,
  HandleTesVariationChangeFunction,
  HandleTimeChangeFunction,
} from "../../types/editOrder";
import { EditReasonBanner } from "../EditReasonBanner";
import { AdditionalInformation } from "./AdditionalInformation";
import { BasicInfo } from "./BasicInfo";
import { Descriptions } from "./Descriptions";
import { Requirements } from "./Requirements";
import { SalaryLimit } from "./SalaryLimit";
import { ShiftInfo } from "./ShiftInfo";
import { Skills } from "./Skills";
import { TermsOfEmployment } from "./TermsOfEmployment";
import { OrderFormShortOtherSettings } from "../../additionalInfo/OrderFormShortOtherSettings";
import { selectOrderRowById } from "../../orderRowsDetailedSlice";

type Props = {
  orderRow: OrderEdit;
  offers: OffersStatus;
  options: AutocompleteOptions;
  openSearchDialog: () => void;
  handleTimeChange: HandleTimeChangeFunction;
  handleCustomerCCChange: HandleCustomerCCChangeFunction;
  handleAmountChange: HandleAmountChangeFunction;
  handleDescriptionsChange: HandleDescriptionsChangeFunction;
  handleAdditionalInfoChange: HandleAdditionalInfoChangeFunction;
  handleSalaryLimitChange: HandleSalaryLimitChangeFunction;
  handlePeriodStartDateChange: HandlePeriodStartDateChangeFunction;
  handleRequirementsChange: HandleRequirementsChangeFunction;
  handleFillShiftChange: HandleFillShiftChangeFunction;
  handleSkillsChange: HandleSkillsChangeFunction;
  handleCancelAssignment: CancelAssignmentCallback;
  handleCancelAllAssignments: CancelAllAssignmentsCallback;
  handleAutomatchingStatusChange: HandleAutomatchingStatusChangeFunction;
  handleAutomatchingDelayChange: HandleAutomatchingDelayChangeFunction;
  handleSupplementsChange: HandleSupplementsChangeFunction;
  handleSupplementPriceOverridesChange: HandleSupplementPriceOverrideChangeFunction;
  handleFlexibleStartTimeChange: HandleFlexibleStartTimeChangeFunction;
  handleTeamNotificationSettingsChange: HandleTeamNotificationSettingsChangeFunction;
  isAutomatchingOn: boolean;
  isExpertAuthorizedToEdit: boolean;
  handleTesVariationChange: HandleTesVariationChangeFunction;
  periodStartDateIsRequired: boolean | null;
  setPeriodStartDateIsRequired: Dispatch<SetStateAction<boolean | null>>;
  setValidationErrors: (errors: ValidationError[]) => void;
  isOrderFulfilled: boolean;
  automatchingDelayWarning?: string;
  automatchingInitialStatus: AutomatchingStatus;
  isReadOnly: boolean;
};

export const EditShortOrderInfo = ({
  orderRow,
  offers,
  options,
  openSearchDialog,
  handleTimeChange,
  handleCustomerCCChange,
  handleAmountChange,
  handleDescriptionsChange,
  handleAdditionalInfoChange,
  handleSalaryLimitChange,
  handlePeriodStartDateChange,
  handleRequirementsChange,
  handleFillShiftChange,
  handleSkillsChange,
  handleCancelAssignment,
  handleCancelAllAssignments,
  handleAutomatchingStatusChange,
  handleAutomatchingDelayChange,
  handleSupplementsChange,
  isAutomatchingOn,
  isExpertAuthorizedToEdit,
  handleTesVariationChange,
  periodStartDateIsRequired,
  setPeriodStartDateIsRequired,
  handleSupplementPriceOverridesChange,
  handleFlexibleStartTimeChange,
  handleTeamNotificationSettingsChange,
  setValidationErrors,
  isOrderFulfilled,
  automatchingDelayWarning,
  automatchingInitialStatus,
  isReadOnly,
}: Props) => {
  const { user } = useAppSelector((state) => state.user);
  const { t } = useTranslation(["order", "customers"]);
  const localized = useAppSelector((state) => state.localized);

  const bundle = useAppSelector((state) =>
    selectOrderRowById(state, orderRow.bundleId || ""),
  );

  const { validationErrors, handleError } = useContext(ShiftOrderContext);
  const [isShowMoreOpen, setIsShowMoreOpen] = useState<boolean>(false);
  const [tesPricingOverridesOpen, setTesPricingOverridesOpen] = useState(false);

  interface Style {
    display: string;
  }

  const noneStyle: Style = { display: "none" };
  const contentsStyle: Style = { display: "contents" };

  const [showMoreStyle, setShowMoreStyle] = useState<Style>(contentsStyle);
  const [showLessStyle, setShowLessStyle] = useState<Style>(noneStyle);

  useEffect(() => {
    const none: Style = { display: "none" };
    const contents: Style = { display: "contents" };
    if (isShowMoreOpen) {
      setShowMoreStyle(none);
      setShowLessStyle(contents);
    } else {
      setShowMoreStyle(contents);
      setShowLessStyle(none);
    }
  }, [isShowMoreOpen]);

  useEffect(() => {
    setValidationErrors(validationErrors);
  }, [validationErrors, setValidationErrors]);

  const {
    workLocationName,
    workLocationDescriptionForEezy,
    amount,
    dateOrdered,
    timestampOrdered,
    title,
    orderer,
    customerOrgName,
    orderRowTemplateName,
    teamNotificationSettings,
  } = orderRow;
  const {
    tesId,
    tesVariation,
    skillIds,
    certificateIds,
    supplements,
    supplementPriceOverrides,
    languageSkills,
    description,
    trainings,
    descriptionEn,
    descriptionForTalent,
    descriptionForTalentEn,
    periodStartDate,
  } = orderRow.orderRows[0].taskSpec;

  const { salaryLimit, time } = orderRow.orderRows[0].shiftDetail;

  const {
    descriptionToEezyAndCustomer,
    descriptionToEezyAndCustomerAndTalent,
  } = orderRow.orderRows[0];

  const basicInfoValues = {
    workLocationName: workLocationName,
    workLocationDescriptionForEezy: workLocationDescriptionForEezy || "",
    amount: amount,
    originalAmount: orderRow.originalAmount ?? undefined,
    createdAt: new Date(dateOrdered).getTime(),
    createdAtWithTimestamp: timestampOrdered,
    title: title || "",
    orderer: orderer || "",
    customerOrgName: customerOrgName || "",
    orderTemplateName: orderRowTemplateName || "",
  };

  // TODO: amountFulfilled missing from orderRowsByBundleId response
  const assignedTalentsAmount = orderRow.shiftDetails.reduce(
    (uniqueTalents, shift) => {
      if (
        shift.assignedTalent &&
        !shift.assignedTalent.reasonForCancellation &&
        !shift.assignedTalent.absent
      ) {
        uniqueTalents.add(shift.assignedTalent.id);
      }
      return uniqueTalents;
    },
    new Set(),
  ).size;

  const requirementsValues = {
    tes: {
      id: tesId,
      name: localized.tes[tesId],
    },
    tesVariation: tesVariation,
    salaryLimit: salaryLimit ?? null,
    skills: skillIds.map((id) => ({
      id,
      name: localized.skill[id],
    })),
    certificates: certificateIds.map((id) => ({
      id,
      name: localized.certificate[id],
    })),
    languages: languageSkills.map((language) => ({
      id: language.languageCode,
      languageCode: language.languageCode,
      skillLevel: language.skillLevel,
    })),
    trainings: trainings?.map((id) => ({
      id,
      name: localized.training[id],
    })),
    supplements: supplements,
    supplementPriceOverrides: supplementPriceOverrides,
  };

  const descriptionValues = {
    description: description,
    descriptionEn: descriptionEn,
    descriptionForTalent: descriptionForTalent,
    descriptionForTalentEn: descriptionForTalentEn,
  };

  // avoid unnecessary tesVariation fetches
  const memoizedTesId = useMemo(() => tesId, [tesId]);

  // avoid unnecessary tesVariation fetches
  const memoizedStartDate = useMemo(() => new Date(time.start), [time.start]);

  const startDate = !!periodStartDate ? new Date(periodStartDate) : null;

  const sortedEditReasons =
    bundle?.reasonsForEdits &&
    [...bundle.reasonsForEdits].sort(
      (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime(),
    );

  const isStartTimeInPast = time.start <= Date.now();
  const isCancelled = !!orderRow.cancelReason?.length;

  const descToEezyAndCustomerDisabled = !isExpertAuthorizedToEdit || isReadOnly;
  const descToEezyAndCustomerAndTalentDisabled =
    !isExpertAuthorizedToEdit || isStartTimeInPast || isCancelled;
  const teamNotificationSettingsDisabled = isOrderFulfilled || isReadOnly;
  const canViewTeamNotificationSettings = isExpertCustomerServiceOrAdmin(user);

  // YLPE-6651: display open shifts correctly.
  // orderRowsByBundleId returns incorrect shift details because of fulfillment status bug,
  // so for now check whether an open one should be displayed per separately fetched fulfillment status.
  const shiftsWithTalent = orderRow.shiftDetails.filter(
    (shift) => !!shift.assignedTalent,
  );
  const singleShiftEmptyRow: ShiftDetails2 = orderRow.shiftDetails.find(
    (shift) => !shift.assignedTalent,
  ) || {
    id: orderRow.orderRows[0].id,
    orderRowId: orderRow.orderRows[0].id,
    shiftDetail: orderRow.orderRows[0].shiftDetail,
    version: orderRow.orderRows[0].version,
  };
  const shiftBundleEmptyRows: ShiftDetails2[] = orderRow.shiftDetails.filter(
    (shift) => !shift.assignedTalent,
  );
  const emptyRows =
    orderRow.orderRows.length > 1
      ? shiftBundleEmptyRows
      : [singleShiftEmptyRow];
  // IF order needs talent, show an empty shift:
  // - one shift for single shift orders, all empty shifts for shift bundles
  // ELSE make sure no empty shifts are shown.
  const shiftRows =
    !isOrderFulfilled && !orderRow.cancelReason
      ? [...shiftsWithTalent, ...emptyRows]
      : orderRow.shiftDetails.filter((shift) => !!shift.assignedTalent);

  return (
    <Grid container spacing={2} minWidth="910px" width="90%" maxWidth="1400px">
      <Grid item xs={12}>
        <AdditionalInfoFormContainer>
          {!isEmpty(sortedEditReasons) ? (
            <FormSection>
              {sortedEditReasons?.map((reason, i) => (
                <EditReasonBanner key={i} reason={reason} />
              ))}
            </FormSection>
          ) : null}
          <BasicInfo
            isExpertAuthorizedToEdit={isExpertAuthorizedToEdit}
            values={basicInfoValues}
            isOrderFulfilled={isOrderFulfilled}
            handleAmountChange={handleAmountChange}
            assignedTalentsAmount={assignedTalentsAmount}
          />
          <ShiftInfo
            isReadOnly={isReadOnly}
            isAutomatchingOn={isAutomatchingOn}
            isExpertAuthorizedToEdit={isExpertAuthorizedToEdit}
            rows={shiftRows}
            options={options}
            handleTimeChange={handleTimeChange}
            handleCustomerCCChange={handleCustomerCCChange}
            validationErrors={validationErrors}
            handleFillShiftChange={handleFillShiftChange}
            cancelAssignment={handleCancelAssignment}
            cancelAllAssignments={handleCancelAllAssignments}
            handleFlexibleStartTimeChange={handleFlexibleStartTimeChange}
          />
          <Box sx={showMoreStyle}>
            <FormSection>
              <Grid item>
                <Button
                  disabled={isShowMoreOpen}
                  onClick={() => setIsShowMoreOpen(true)}
                >
                  <KeyboardArrowDownIcon />
                  {t("common:showMoreInformation")}
                </Button>
              </Grid>
            </FormSection>
          </Box>
          <Collapse in={isShowMoreOpen} timeout="auto" unmountOnExit>
            <FormSection title={t("termsOfEmployment")}>
              <TermsOfEmployment
                reasonForTemporaryEmployment={
                  orderRow.reasonForTemporaryEmployment
                }
                tesId={memoizedTesId}
                date={memoizedStartDate}
                tesVariations={tesVariation}
                onTesVariationChange={handleTesVariationChange}
              />
            </FormSection>
            <FormSection title={t("common:requirements")}>
              <Requirements
                disableEdit={
                  isAutomatchingOn || !isExpertAuthorizedToEdit || isReadOnly
                }
                certificates={requirementsValues.certificates}
                languages={requirementsValues.languages}
                trainings={requirementsValues.trainings}
                onRequirementsChange={handleRequirementsChange}
              />
            </FormSection>
            <FormSection title={t("common:requiredSkills")}>
              <Skills
                disableEdit={
                  isAutomatchingOn || !isExpertAuthorizedToEdit || isReadOnly
                }
                skills={requirementsValues.skills}
                onRequirementsChange={handleSkillsChange}
              />
            </FormSection>
            <FormSection title={t("common:description")}>
              <Descriptions
                isAutomatchingOn={isAutomatchingOn}
                isExpertAuthorizedToEdit={isExpertAuthorizedToEdit}
                isReadOnly={isReadOnly}
                values={descriptionValues}
                handleDescriptionsChange={handleDescriptionsChange}
              />
            </FormSection>
            <FormSection title={t("common:additionalInfo")}>
              <AdditionalInformation
                descriptionToEezyAndCustomerDisabled={
                  descToEezyAndCustomerDisabled
                }
                descriptionToEezyAndCustomerAndTalentDisabled={
                  descToEezyAndCustomerAndTalentDisabled
                }
                info={{
                  descriptionToEezyAndCustomer,
                  descriptionToEezyAndCustomerAndTalent,
                }}
                handleInfoChange={handleAdditionalInfoChange}
              />
            </FormSection>
            <FormSection title={t("order:salaryLimit")}>
              <SalaryLimit
                disableEdit={
                  isAutomatchingOn || !isExpertAuthorizedToEdit || isReadOnly
                }
                salaryLimit={salaryLimit}
                periodStartDate={startDate}
                periodStartDateIsRequired={periodStartDateIsRequired}
                handleSalaryLimitChange={handleSalaryLimitChange}
                handlePeriodStartDateChange={handlePeriodStartDateChange}
              />
            </FormSection>
            <FormSection title={t("talentProfile:showToCustomerSupplement")}>
              <OrderSupplements
                tesId={tesId}
                tesVariation={tesVariation}
                supplements={supplements || {}}
                onOrderTemplateChange={handleSupplementsChange}
                editable={false}
                setPeriodStartDateIsRequired={setPeriodStartDateIsRequired}
              />
            </FormSection>
            <FormSection
              title={t("order:salaryCategoryValues")}
              collapse
              open={tesPricingOverridesOpen}
              toggleOpen={() =>
                setTesPricingOverridesOpen(!tesPricingOverridesOpen)
              }
            >
              <OrderShortTesPricingOverride
                editable={false}
                supplementPriceOverrides={supplementPriceOverrides}
                tesId={tesId}
                onOrderTemplateChange={handleSupplementPriceOverridesChange}
              />
            </FormSection>
            <OrderFormShortOtherSettings
              isBillable={orderRow.shiftDetails[0]?.shiftDetail.isBillable}
              onIsBillableChange={() => {}}
              tooltipText={
                t("customers:orderTemplate:notBilledTooltip") +
                " " +
                t("order:notBilledAdditionalInfo")
              }
              disabled
            />
            {canViewTeamNotificationSettings && (
              <TeamNotificationSettings
                isEditing
                disableEdit={teamNotificationSettingsDisabled}
                onTeamNotificationSettingsChange={
                  handleTeamNotificationSettingsChange
                }
                validationErrors={validationErrors}
                fieldId={orderRow.bundleId}
                shiftDetails={orderRow.shiftDetails}
                handleError={handleError}
                notifyMinutesBeforeIfNotFulfilled={
                  teamNotificationSettings?.notifyMinutesBeforeIfNotFulfilled ||
                  0
                }
                notifyWhenFulfilled={
                  teamNotificationSettings?.notifyWhenFulfilled || false
                }
              />
            )}
            <Box sx={showLessStyle}>
              <FormSection>
                <Grid container item>
                  <Button onClick={() => setIsShowMoreOpen(false)}>
                    <KeyboardArrowUpIcon />
                    {t("common:showLessInformation")}
                  </Button>
                </Grid>
              </FormSection>
            </Box>
          </Collapse>
        </AdditionalInfoFormContainer>
      </Grid>
      <Grid item xs={12}>
        <OffersStatusBlock
          offers={offers}
          disableAutomatchingToggle={
            isReadOnly || isAutomatchingOn || !isExpertAuthorizedToEdit
          }
          disableManualOffers={
            isReadOnly || isAutomatchingOn || !isExpertAuthorizedToEdit
          }
          bundleId={orderRow.bundleId || ""}
          openSearchDialog={openSearchDialog}
          handleAutomatchingStatusChange={handleAutomatchingStatusChange}
          automatchingInitialStatus={automatchingInitialStatus}
          automatchingStartDelayMinutes={
            orderRow.status?.automatchingStartDelayMinutes
          }
          handleAutomatchingDelayChange={handleAutomatchingDelayChange}
          automatchingDelayWarning={automatchingDelayWarning}
        />
      </Grid>
    </Grid>
  );
};
